import { supabase } from '../lib/supabase';


/**
 * Authentication Service
 * Handles all authentication operations including sign in, sign up, sign out, and password management
 */

export const authService = {
  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - { data, error }
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign out current user
   * @returns {Promise<Object>} - { error }
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  },

  /**
   * Sign up new user
   * @param {Object} userData - User data including email, password, fullName
   * @returns {Promise<Object>} - { data, error }
   */
  async signUp(userData) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email: userData?.email,
        password: userData?.password,
        options: {
          emailRedirectTo: `${window?.location?.origin}/user-login?verified=true`,
          data: {
            full_name: userData?.fullName,
            avatar_url: userData?.avatarUrl || ''
          }
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get current session
   * @returns {Promise<Object>} - { data: { session }, error }
   */
  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Get session error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get current user
   * @returns {Promise<Object>} - { data: { user }, error }
   */
  async getUser() {
    try {
      const { data, error } = await supabase?.auth?.getUser();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Get user error:', error);
      return { data: null, error };
    }
  },

  /**
   * Reset password for email
   * @param {string} email - User email
   * @returns {Promise<Object>} - { data, error }
   */
  async resetPassword(email) {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window?.location?.origin}/reset-password`
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - { data, error }
   */
  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error };
    }
  }
};